import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Table, Select, Divider } from '_atoms';
import { TableMobile } from '_atoms/Table/Table';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { ReactComponent as Vector } from 'dist/icons/Vector1.svg';
import { PLACEMENTS, SelectSmall } from '_atoms/SelectSmall';
import type { SelectSmallOption } from '_atoms/SelectSmall';
import type { TableColumnType, TableRow } from '_atoms/Table/types';
import type { Opening } from 'store/reducers/jobReducer';
import { SELECT_TYPES } from '_atoms/Select/constants';
import { PopupApproveOpening, PopupCloseDeclineOpening } from '_molecules';
import useScreenResize from 'helpers/useScreenResize';
import { ROLES } from 'permission';
import { declineApproveOpening, getAllCompanies, getLMOpeningsTableDashboardAPI } from 'actions';
import { getCompanies } from 'store/selectors/commonSelectors';
import { getUser } from 'store/selectors/authorizeSelectors';
import type { AppDispatch } from 'store';
import { ReactComponent as JobImage } from 'dist/emptyStates/dashboardOpening.svg';
import { EXPECTED_DATES } from '_templates/CreateOpening/constants';
import type {
  DashboardSelectOption as SelectOption,
  DashboardTableOptions as TableOptions,
  DashboardGroupingOption as GroupingOption,
  OpeningDeclinePopupState,
  OpeningApprovePopupState,
  DashboardOpeningsGroupRow,
} from '_templates/dashboardTypes';
import {
  INITIAL_VALUES_1,
  OPENINGS_GROUP,
  OPENING_STATUSES_OPTIONS,
  OPENINGS_TABLES_MAPPERS,
  OPENINGS_TABLES_COLUMNS,
  OPENINGS_TABLES_COLUMNS_TOP,
  COMPANY_ALL,
} from './constants';
import './style.scss';

function OpeningsTableEmpty() {
  const { t } = useTranslation();

  return (
    <div className="all-openings__empty">
      <JobImage />
      <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:dashboard_location_manager.text_no_openings')}
      </Typography.Title3>
      <Typography.Text>{t('translation:dashboard_location_manager.text_no_openings_hint')}</Typography.Text>
    </div>
  );
}

function AllOpeningsService() {
  const dispatch = useDispatch<AppDispatch>();
  const tableRef = useRef<HTMLDivElement>(null);
  const tableRequestIdRef = useRef(0);
  const { t } = useTranslation();

  const user = useSelector(getUser)!;
  const isLmApprover = user.roles.find(r => r.name === ROLES.LOCATION_MANAGER)?.is_opening_approver;

  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<Record<string, unknown>[]>([]);
  const [tableOptions, setTableOptions] = useState<TableOptions>(INITIAL_VALUES_1);
  const [openingStatus, setOpeningStatus] = useState<SelectOption[]>(OPENING_STATUSES_OPTIONS);
  const [, setCount] = useState(0);
  const [company, setCompany] = useState<SelectSmallOption>(COMPANY_ALL);
  const [grouping, setGrouping] = useState<GroupingOption>(OPENINGS_GROUP[0] as GroupingOption);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [showOpeningDeclinePopup, setShowOpeningDeclinePopup] = useState<OpeningDeclinePopupState>({
    open: false,
  });
  const [openApprovePopup, setOpenApprovePopup] = useState<OpeningApprovePopupState>({ open: false });

  const companies = useSelector(getCompanies);
  const companyOptions = useMemo(
    () => [COMPANY_ALL, ...companies.map(c => ({ value: c.uuid, label: c.name }))],
    [companies]
  );

  const onExpand =
    ({ uuid }: Record<string, unknown>) =>
    () => {
      const rowUuid = uuid as string;
      if (!expandedRowKeys.includes(rowUuid)) setExpandedRowKeys(rows => [...rows, rowUuid]);
      else setExpandedRowKeys(rows => rows.filter(r => r !== rowUuid));
    };

  const onClickTitle =
    ({ uuid, name }: Record<string, unknown>) =>
    () => {
      if (grouping.value === OPENINGS_GROUP[0].value) {
        window.open(`/jobs?services=[{"value":"${uuid}","label":"${name}"}]`, '_blank', 'noopener noreferrer');
      } else if (grouping.value === OPENINGS_GROUP[1].value) {
        window.open(`/jobs/${uuid}/show/openings`, '_blank', 'noopener noreferrer');
      } else {
        window.open(`/jobs?offices=[{"value":"${uuid}","label":"${name}"}]`, '_blank', 'noopener noreferrer');
      }
    };

  const onChangeGrouping = (newGrouping: GroupingOption) => {
    setGrouping(g => ({ ...newGrouping, tableData: g.tableData }));
  };

  const getTableData = (options: TableOptions = tableOptions) => {
    tableRequestIdRef.current += 1;
    const requestId = tableRequestIdRef.current;
    setLoading(true);
    getLMOpeningsTableDashboardAPI(
      {
        limit: options.limit.value,
        offset: options.offset,
        status: openingStatus.map(({ value }) => value).join(','),
        company: company.value as string,
      },
      grouping.value
    )
      .then(d => {
        if (requestId !== tableRequestIdRef.current) return;
        setTableData([]);
        setTableOptions(o => ({ ...o, itemsCount: d.count }));
        setCount((d.results as DashboardOpeningsGroupRow[]).reduce((c, row) => c + row.openings.length, 0));
        setGrouping(g => ({ ...g, tableData: g.value }));
        setTableData(
          OPENINGS_TABLES_MAPPERS[grouping.value](d.results as DashboardOpeningsGroupRow[]) as Record<string, unknown>[]
        );
        setExpandedRowKeys(d.results.map(({ uuid }) => uuid));
      })
      .catch(() => {
        if (requestId !== tableRequestIdRef.current) return;
        setTableData([]);
        setTableOptions(INITIAL_VALUES_1);
        setCount(0);
        setGrouping(g => ({ ...g, tableData: g.value }));
      })
      .finally(() => {
        if (requestId !== tableRequestIdRef.current) return;
        setLoading(false);
      });
  };

  const onChangeFilter = (field: string, item: unknown) => {
    if (field === 'page') {
      const options = {
        ...tableOptions,
        page: item as number,
        offset: ((item as number) - 1) * tableOptions.limit.value,
      };
      setTableOptions(options);
      getTableData(options);
    } else if (field === 'limit') {
      const options = { ...tableOptions, limit: item as { value: number; label: number }, offset: 0, page: 1 };
      setTableOptions(options);
      getTableData(options);
    } else {
      const options = { ...tableOptions, [field]: item };
      setTableOptions(options);
      getTableData(options);
    }
  };

  const onOpeningAction = ({ job_uuid, service_uuid, opening, ...d }: Record<string, unknown>, type: string) => {
    const openingData = opening as Record<string, unknown>;
    if (type === 'approve') {
      const show =
        Math.abs(moment(openingData.expected as string, 'YYYY-MM-DD').diff(moment(), 'days')) <
        Math.abs(EXPECTED_DATES[openingData.level as string]?.diff(moment(), 'days'));
      if (show) {
        setOpenApprovePopup({
          open: true,
          job_uuid: job_uuid as string,
          service_uuid: service_uuid as string,
          type,
        });
      } else {
        declineApproveOpening(job_uuid as string, service_uuid as string, type, {}).then(() => getTableData());
      }
    } else {
      setShowOpeningDeclinePopup({
        open: true,
        job_uuid: job_uuid as string,
        service_uuid: service_uuid as string,
        opening: opening as Opening,
        type,
        job_name: d.job_name as string | undefined,
      });
    }
  };

  const onCloseCloseOpeningPopup = () => setShowOpeningDeclinePopup({ open: false });
  const onDeclineOpening = (reason: string) => {
    if (!showOpeningDeclinePopup.open) return;
    const { job_uuid, service_uuid, type } = showOpeningDeclinePopup;
    declineApproveOpening(job_uuid, service_uuid, type, { reason }).then(() => getTableData());
    setShowOpeningDeclinePopup({ open: false });
  };

  const onCloseApproveConfirmation = () => setOpenApprovePopup({ open: false });
  const onApproveConfirmationPopup = () => {
    if (!openApprovePopup.open) return;
    declineApproveOpening(openApprovePopup.job_uuid, openApprovePopup.service_uuid, openApprovePopup.type, {}).then(
      () => getTableData()
    );
    setOpenApprovePopup({ open: false });
  };

  useEffect(() => {
    dispatch(getAllCompanies());
  }, [dispatch]);
  useEffect(() => {
    getTableData();
  }, [tableOptions.limit, tableOptions.offset, tableOptions.page, openingStatus, grouping.value, company]);
  useScreenResize('md', tableRef);

  return (
    <>
      <Table
        innerRef={tableRef}
        rowKey="uuid"
        className={cn('all-openings', grouping.tableData)}
        cardTitle={t('translation:dashboard_location_manager.card_title_openings')}
        cardCount={tableOptions.itemsCount}
        emptyComponent={<OpeningsTableEmpty />}
        tooltipInfo={
          grouping.tableData === OPENINGS_GROUP[2].tableData
            ? t('translation:dashboard_location_manager.tooltip_info_multiple_locations')
            : undefined
        }
        extra={
          <>
            <Select
              labelInValue
              placeholder={t('translation:dashboard_location_manager.ph_select_status')}
              options={OPENING_STATUSES_OPTIONS}
              value={openingStatus}
              onChange={setOpeningStatus}
              mode={SELECT_TYPES.MULTIPLE}
            />
            <div className="filter-selectors">
              <div className="filter-container">
                <SelectSmall
                  label={t('translation:dashboard_location_manager.label_company')}
                  options={companyOptions}
                  value={company}
                  onChange={(option: SelectSmallOption) => setCompany(option)}
                  placement={PLACEMENTS.BOTTOM_RIGHT}
                />
              </div>
              <Divider type="vertical" />
              <div className="filter-container">
                <SelectSmall
                  label={t('translation:dashboard_location_manager.label_group_by')}
                  options={OPENINGS_GROUP}
                  value={grouping}
                  onChange={onChangeGrouping}
                  placement={PLACEMENTS.BOTTOM_RIGHT}
                />
              </div>
            </div>
          </>
        }
        showCardFooter={!!tableData.length}
        columns={
          isLmApprover
            ? (OPENINGS_TABLES_COLUMNS_TOP[grouping.tableData] as TableColumnType[])
            : (OPENINGS_TABLES_COLUMNS_TOP[grouping.tableData].slice(0, -1) as TableColumnType[])
        }
        loading={loading}
        data={tableData as TableRow[]}
        limit={tableOptions.limit}
        page={tableOptions.page}
        itemsCount={tableOptions.itemsCount}
        updateParams={onChangeFilter}
        expandable={{
          expandedRowKeys,
          // eslint-disable-next-line react/no-unstable-nested-components
          expandedRowRender: ({ openings }: Record<string, unknown>) => (
            <>
              <Table
                rowKey="uuid"
                className={cn('all-openings__sub-table', grouping.tableData)}
                columns={
                  isLmApprover
                    ? (OPENINGS_TABLES_COLUMNS[grouping.tableData](
                        onOpeningAction,
                        !!isLmApprover
                      ) as TableColumnType[])
                    : (OPENINGS_TABLES_COLUMNS[grouping.tableData](onOpeningAction, !!isLmApprover).slice(
                        0,
                        -1
                      ) as TableColumnType[])
                }
                data={openings as TableRow[]}
                showCardHeader={false}
                showCardFooter={false}
              />
              <TableMobile
                className={cn('all-openings__sub-table all-openings__sub-table-mobile', grouping.tableData)}
                data={openings as TableRow[]}
                columns={
                  isLmApprover
                    ? (OPENINGS_TABLES_COLUMNS[grouping.tableData](
                        onOpeningAction,
                        !!isLmApprover
                      ) as TableColumnType[])
                    : (OPENINGS_TABLES_COLUMNS[grouping.tableData](onOpeningAction, !!isLmApprover).slice(
                        0,
                        -1
                      ) as TableColumnType[])
                }
              />
            </>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          expandIcon: ({ expanded, record }) => (
            <div role="none" className="expanded-row">
              <div className="expanded-row__info">
                <Vector className={cn('expanded-row__icon', { expanded })} onClick={onExpand(record)} />
                <Typography.Text className="name" onClick={onClickTitle(record)}>
                  {record.name}
                  <span>{record.count}</span>
                </Typography.Text>
                {!!record.service && <Typography.Text className="name__extra">{record.service}</Typography.Text>}
              </div>
              <div className="expanded-row__extra">
                {record.candidates_count !== undefined && (
                  <Typography.Caption>
                    {(t as (key: string, options?: Record<string, unknown>) => string)(
                      'translation:dashboard_location_manager.text_total_active_candidates',
                      { count: record.candidates_count as number }
                    )}
                  </Typography.Caption>
                )}
                <Typography.Caption>
                  {(t as (key: string, options?: Record<string, unknown>) => string)(
                    'translation:dashboard_location_manager.text_total_openings',
                    { count: record.openings_count as number }
                  )}
                </Typography.Caption>
              </div>
            </div>
          ),
        }}
      />
      <PopupCloseDeclineOpening
        open={showOpeningDeclinePopup.open}
        opening={showOpeningDeclinePopup.open ? showOpeningDeclinePopup.opening : undefined}
        jobName={showOpeningDeclinePopup.open ? showOpeningDeclinePopup.job_name ?? '' : ''}
        onClose={onCloseCloseOpeningPopup}
        confirm={onDeclineOpening}
      />
      <PopupApproveOpening
        open={openApprovePopup.open}
        onOk={onApproveConfirmationPopup}
        onClose={onCloseApproveConfirmation}
      />
    </>
  );
}

export const DashboardLocationManager = () => (
  <div className="dashboard-location-manager">
    <AllOpeningsService />
  </div>
);
